import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Trash2, Check, CheckCheck, FileText, Download } from 'lucide-react';
import { useChatContext } from 'stream-chat-react';
import PowerBIFilterShareMessage from './PowerBIFilterShareMessage';
import NativeReportShareMessage from './NativeReportShareMessage';
import AIChatShareMessage from './AIChatShareMessage';
import styles from '../Chat.module.css';

const AVATAR_COLORS = [
  'var(--lodge-orange-400)', '#7C5CFC', '#12B76A', '#0BA5EC',
  '#F04438', '#F79009', '#6172F3', '#EE46BC',
];

const getAvatarColor = (id) => {
  const hash = (id || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const REACTION_EMOJI = {
  like: '👍',
  love: '❤️',
  haha: '😂',
  wow: '😲',
  sad: '😔',
};

const CustomMessageItem = ({ message, isOwn, channel, showActions, currentUserId, onToggleReaction }) => {
  const [showDeleteMenu, setShowDeleteMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const menuRef = useRef(null);
  const { client } = useChatContext();

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleDeleteMessage = async () => {
    if (!channel || !message || isDeleting) return;

    const currentUser = client?.user;
    if (!currentUser || message.user?.id !== currentUser.id) {
      alert('You can only delete your own messages.');
      setShowDeleteMenu(false);
      return;
    }

    setIsDeleting(true);
    try {
      if (client?.deleteMessage) {
        await client.deleteMessage(message.id);
      } else if (message.delete && typeof message.delete === 'function') {
        await message.delete();
      } else {
        throw new Error('No valid delete method available');
      }
      setShowDeleteMenu(false);
    } catch (error) {
      console.error('[CustomMessageItem] Error deleting message:', error);
      if (error.code === 4 || error.status === 403) {
        alert('You do not have permission to delete this message.');
      } else if (error.code === 16 || error.status === 404) {
        alert('This message has already been deleted.');
      } else {
        alert(`Failed to delete message: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setShowDeleteMenu(false);
    };
    if (showDeleteMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDeleteMenu]);

  // Check for PowerBI filter share attachment
  const hasPowerBIAttachment = message.attachments &&
    message.attachments.some(att => att.type === 'pbi_filter_share');

  if (hasPowerBIAttachment) {
    return <PowerBIFilterShareMessage message={message} />;
  }

  // Check for AI chat session share attachment
  const hasAIChatShareAttachment = message.attachments &&
    message.attachments.some(att => att.type === 'ai_session_share');

  if (hasAIChatShareAttachment) {
    return <AIChatShareMessage message={message} />;
  }

  // Check for native report view share attachment
  const hasNativeReportShare = message.attachments &&
    message.attachments.some(att => att.type === 'native_report_share');

  if (hasNativeReportShare) {
    return <NativeReportShareMessage message={message} />;
  }

  const getInitials = () => {
    const name = message.user?.name || message.user?.id || 'U';
    const names = name.split(' ');
    if (names.length > 1) return `${names[0][0]}${names[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const formatFileSize = (bytes) => {
    const n = Number(bytes || 0);
    if (!n) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const normalAttachments = (message.attachments || [])
    .filter((attachment) => !['pbi_filter_share', 'ai_session_share', 'native_report_share'].includes(attachment.type));
  const hasText = Boolean(message.text?.trim());
  const attachmentOnly = !hasText && normalAttachments.length > 0;

  const reactionCounts = message.reaction_counts || {};
  const ownReactionTypes = new Set((message.own_reactions || [])
    .filter((reaction) => !currentUserId || reaction.user_id === currentUserId || reaction.user?.id === currentUserId)
    .map((reaction) => reaction.type));
  const reactionEntries = Object.entries(reactionCounts)
    .filter(([type, count]) => count > 0 && REACTION_EMOJI[type]);

  return (
    <>
      {!isOwn && (
        <div style={{ flexShrink: 0, marginRight: 'var(--sp-3)' }}>
          <div
            className={`${styles.avatar} ${styles.avatarSm}`}
            style={{ background: getAvatarColor(message.user?.id) }}
          >
            {getInitials()}
          </div>
        </div>
      )}

      <div style={{ maxWidth: attachmentOnly ? 420 : '70%', position: 'relative' }}>
        {!isOwn && (
          <div className={styles.messageSender}>
            {message.user?.name || message.user?.id}
          </div>
        )}

        <div className={`${styles.messageBubble} ${isOwn ? styles.outgoing : styles.incoming} ${attachmentOnly ? styles.attachmentOnly : ''}`}>
          {hasText ? (
            <p style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {message.text}
            </p>
          ) : null}

          {normalAttachments.length > 0 && (
            <div className={styles.messageAttachments}>
              {normalAttachments.map((attachment, index) => {
                const url = attachment.image_url || attachment.asset_url || attachment.title_link;
                const title = attachment.title || attachment.name || 'Attachment';
                const isImage = attachment.type === 'image' || attachment.mime_type?.startsWith?.('image/');
                return (
                  <a
                    key={`${url || title}-${index}`}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.messageAttachment}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {isImage && url ? (
                      <img src={url} alt={title} className={styles.messageAttachmentImage} />
                    ) : (
                      <div className={styles.messageAttachmentIcon}>
                        <FileText size={16} />
                      </div>
                    )}
                    <div className={styles.messageAttachmentText}>
                      <span className={styles.messageAttachmentTitle}>{title}</span>
                      {attachment.file_size ? (
                        <span className={styles.messageAttachmentMeta}>{formatFileSize(attachment.file_size)}</span>
                      ) : null}
                    </div>
                    <Download size={14} className={styles.messageAttachmentDownload} />
                  </a>
                );
              })}
            </div>
          )}

          <div className={`${styles.messageTime} ${isOwn ? styles.outgoing : styles.incoming}`}>
            <span>{formatTime(message.created_at)}</span>
            {isOwn && (
              <span>
                {message.status === 'received' ? <CheckCheck size={14} /> : <Check size={14} />}
              </span>
            )}
          </div>

          {reactionEntries.length > 0 && (
            <div className={`${styles.reactionChips} ${isOwn ? styles.own : ''}`}>
              {reactionEntries.map(([type, count]) => {
                const selected = ownReactionTypes.has(type);
                return (
                  <button
                    key={type}
                    type="button"
                    className={`${styles.reactionChip} ${selected ? styles.selected : ''}`}
                    onClick={() => onToggleReaction?.(message, type)}
                    title={selected ? 'Remove reaction' : 'React'}
                  >
                    <span>{REACTION_EMOJI[type]}</span>
                    <span>{count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Delete button for own messages */}
          {isOwn && showActions && (
            <div style={{ position: 'absolute', top: -28, right: -8 }}>
              <button
                onClick={() => setShowDeleteMenu(!showDeleteMenu)}
                className={styles.messageActionBtn}
                style={{
                  background: 'var(--lodge-white)',
                  border: '1px solid var(--lodge-onyx-100)',
                  boxShadow: 'var(--shadow-sm)',
                  borderRadius: 'var(--radius-md)',
                  width: 28,
                  height: 28,
                }}
                title="Message options"
              >
                <MoreVertical size={14} />
              </button>

              {showDeleteMenu && (
                <div ref={menuRef} className={styles.dropdown} style={{ top: 32, right: 0, minWidth: 140 }}>
                  <button
                    onClick={handleDeleteMessage}
                    disabled={isDeleting}
                    className={`${styles.dropdownItem} ${styles.danger}`}
                    style={{ opacity: isDeleting ? 0.5 : 1 }}
                  >
                    {isDeleting ? (
                      <>
                        <div className={styles.spinner} style={{ width: 14, height: 14, borderWidth: 2, borderTopColor: 'var(--color-error)' }} />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={14} />
                        Delete Message
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CustomMessageItem;
